import { Extension } from '@tiptap/core';
import { Node } from 'prosemirror-model';
import { Plugin, PluginKey, Transaction } from 'prosemirror-state';

interface TableInfo {
  maxCols: number;
  hasAnyColwidth: boolean;
  existingWidths: (number | null)[];
}

/**
 * TableColwidthSync Extension
 *
 * Automatically syncs colwidth attributes across all table cells when any column is resized.
 * Ensures consistent colwidth values for proper responsive behavior.
 */
export const TableColwidthSync = Extension.create({
  name: 'tableColwidthSync',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: new PluginKey('tableColwidthSync'),

        appendTransaction: (transactions, _oldState, newState) => {
          if (!transactions.some((tr) => tr.docChanged)) {
            return null;
          }

          // Check if any transaction involved table modifications
          const hasTableChanges = transactions.some((tr) => {
            let hasChanges = false;
            tr.doc.descendants((node) => {
              if (
                node.type.name === 'table' ||
                node.type.name === 'tableCell' ||
                node.type.name === 'tableHeader'
              ) {
                hasChanges = true;
                return false;
              }
            });
            return hasChanges;
          });

          if (!hasTableChanges) {
            return null;
          }

          const tr = newState.tr;
          let hasModifications = false;

          // Sync colwidths for each table
          const syncTableColwidths = (tableNode: Node, tablePos: number): boolean => {
            const tableInfo = analyzeTableStructure(tableNode);

            if (!tableInfo.hasAnyColwidth) {
              return false;
            }

            const finalColwidths = calculateFinalColwidths(tableInfo);

            if (!finalColwidths.length) {
              return false;
            }

            return applyCellColwidths(tableNode, tablePos, tr, finalColwidths);
          };

          newState.doc.descendants((node, pos) => {
            if (node.type.name === 'table') {
              const modifications = syncTableColwidths(node, pos);
              if (modifications) {
                hasModifications = true;
              }
            }
          });

          return hasModifications ? tr : null;
        },
      }),
    ];
  },
});

/**
 * Analyze table structure and collect existing colwidth information
 */
function analyzeTableStructure(tableNode: Node): TableInfo {
  let maxCols = 0;
  let hasAnyColwidth = false;
  const existingWidths: (number | null)[] = [];

  for (let rowIndex = 0; rowIndex < tableNode.content.childCount; rowIndex++) {
    const row = tableNode.content.child(rowIndex);
    let colIndex = 0;

    for (let cellIndex = 0; cellIndex < row.content.childCount; cellIndex++) {
      const cell = row.content.child(cellIndex);
      const cellColwidth = cell.attrs.colwidth;
      const cellColspan = cell.attrs.colspan || 1;

      if (cellColwidth && Array.isArray(cellColwidth) && cellColwidth.length > 0) {
        hasAnyColwidth = true;
        cellColwidth.forEach((width: number, idx: number) => {
          if (typeof width === 'number' && width > 0) {
            existingWidths[colIndex + idx] = width;
          }
        });
      }

      colIndex += cellColspan;
    }

    maxCols = Math.max(maxCols, colIndex);
  }

  return {
    maxCols,
    hasAnyColwidth,
    existingWidths,
  };
}

/**
 * Calculate final colwidths ensuring all columns have values
 */
function calculateFinalColwidths(tableInfo: TableInfo): number[] {
  const { maxCols, existingWidths } = tableInfo;

  if (maxCols === 0) {
    return [];
  }

  // Calculate default width for missing columns
  const existingWidthValues = existingWidths.filter(
    (w): w is number => w !== null && w !== undefined,
  );

  let defaultWidth = 150;
  if (existingWidthValues.length > 0) {
    const avgWidth =
      existingWidthValues.reduce((sum, w) => sum + w, 0) / existingWidthValues.length;
    defaultWidth = Math.max(100, Math.min(300, Math.round(avgWidth)));
  }

  const finalWidths: number[] = [];
  for (let i = 0; i < maxCols; i++) {
    finalWidths[i] = existingWidths[i] || defaultWidth;
  }

  return finalWidths;
}

/**
 * Apply calculated colwidths to all table cells
 */
function applyCellColwidths(
  tableNode: Node,
  tablePos: number,
  tr: Transaction,
  finalWidths: number[],
): boolean {
  let hasChanges = false;
  let currentPos = tablePos + 1;

  for (let rowIndex = 0; rowIndex < tableNode.content.childCount; rowIndex++) {
    const row = tableNode.content.child(rowIndex);
    currentPos += 1;
    let colIndex = 0;

    for (let cellIndex = 0; cellIndex < row.content.childCount; cellIndex++) {
      const cell = row.content.child(cellIndex);
      const cellColspan = cell.attrs.colspan || 1;

      const requiredColwidths = finalWidths.slice(colIndex, colIndex + cellColspan);

      const currentColwidths = cell.attrs.colwidth || [];
      const needsUpdate =
        requiredColwidths.length !== currentColwidths.length ||
        !requiredColwidths.every((w, idx) => w === currentColwidths[idx]);

      if (needsUpdate) {
        tr.setNodeMarkup(currentPos, null, {
          ...cell.attrs,
          colwidth: requiredColwidths,
        });
        hasChanges = true;
      }

      colIndex += cellColspan;
      currentPos += cell.nodeSize;
    }

    currentPos += 1;
  }

  return hasChanges;
}

export default TableColwidthSync;
